import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import {
  type GanttBar,
  type GanttLink,
  type GanttView,
  type ScheduleImpactTask,
  type ScheduleImpactView,
  type TaskGroupView,
} from '@apm/contracts';
import { StoryPointBadge } from '../../tasks/components/story-point-badge';
import { cn } from '../../../lib/utils';
import { isOffPlan } from '../../tasks/off-plan';
import { useNarrowScreen } from '../../projects/components/project-drawer';
import { MilestoneMoveDialog } from './milestone-move-dialog';
import { type DateRange, type DragMode, type DragState, applyDrag, hasMoved } from '../drag';
import {
  type Zoom,
  addDays,
  createScale,
  dateToX,
  formatDate,
  xToDate,
  parseDate,
  ticks,
  visibleRows,
  weekendBands,
  widenWindow,
} from '../scale';
import { type GanttMarker } from '../markers';
import { DRAG_TASK_MIME } from '../../tasks/components/schedule-drawer';
import {
  type BoardFilter,
  NO_BOARD_FILTER,
  isFilterActive,
  matchesFilter,
} from '../../board/filter';
import { type ScheduleHealth, scheduleHealth } from '../schedule-health';
import { displayDate, today as orgToday } from '../../../lib/date';
import { completedOn, isCompletedLate } from '../../../lib/overdue';

/**
 * Bốn mức tình trạng lịch → bốn lớp `fill`.
 *
 * Viết sẵn thành bảng tra thay vì ghép `'fill-rag-' + health` lúc chạy:
 * Tailwind quét **mã nguồn** để biết sinh lớp nào, nên lớp ghép lúc chạy không
 * tồn tại và thanh sẽ trong suốt — bẫy đã ghi ở CLAUDE.md.
 *
 * `/70` cho việc đã xong: nó lùi lại một bậc so với việc đang chạy, để mắt tìm
 * phần còn lại trước. Màu xanh chứ không phải xám — xám ở đây đã có nghĩa khác
 * (thanh baseline mờ bên dưới).
 */
const HEALTH_FILL: Record<ScheduleHealth, string> = {
  done: 'fill-rag-green/70',
  // Đỏ nhưng lùi một bậc như `done`: xong rồi thì không còn là việc cần xử lý,
  // nhưng vẫn phải đọc ra được là nó đã trễ.
  doneLate: 'fill-rag-red/50',
  overdue: 'fill-rag-red',
  behind: 'fill-rag-amber',
  onTrack: 'fill-primary',
};

/** Bảng tra tương ứng cho hình thoi mốc — xem docblock ở chỗ dùng. */
const MILESTONE_FILL: Record<ScheduleHealth, string> = {
  done: 'fill-rag-green',
  // Hình thoi nhỏ quá để mờ đi (xem chỗ dùng): giữ xanh "đã đạt", viền đỏ là "đạt trễ".
  doneLate: 'fill-rag-green stroke-rag-red stroke-2',
  overdue: 'fill-rag-red',
  behind: 'fill-rag-amber',
  onTrack: 'fill-foreground',
};

/**
 * Chiều cao một hàng — APM-GANTT-f.
 *
 * 28 chứ không phải 36: một dự án thật có vài chục việc, và ở 36px thì màn hình
 * chỉ chứa được mười lăm hàng — không đủ để **nhìn ra hình dạng** của kế hoạch,
 * mà đó lại là toàn bộ lý do người ta mở Gantt thay vì mở danh sách.
 *
 * ⚠️ Không hạ thấp hơn nữa: hàng còn phải chứa một ô tích, một nhãn chữ và một
 * thanh 16px, và mỗi thanh là một đích **kéo thả**. Dưới khoảng này thì hai hàng
 * kề nhau bắt đầu tranh nhau cú bấm, và người dùng dời nhầm việc.
 */
/**
 * Chiều cao dải nhãn thời gian, và của hàng tiêu đề cột bên trái — `h-10`.
 *
 * Hai bên phải khai **cùng một** con số: bên phải dải nhãn `sticky` nằm *trong*
 * vùng cuộn, còn bên trái tiêu đề nằm *ngoài* cửa sổ hàng. Nên cửa sổ bên trái
 * phải cao đúng bằng `clientHeight` của bên phải **trừ đi** dải nhãn ấy — không
 * trừ thì danh sách dài hơn biểu đồ đúng 40px, tràn khỏi khung, và phần thừa bị
 * cắt cụt ở đáy.
 */
const HEADER_HEIGHT = 40;

const ROW_HEIGHT = 28;
const BAR_HEIGHT = 16;
/**
 * Kiểu dữ liệu của cú kéo **xếp lại hàng** — APM-GANTT-h.
 *
 * Khác `DRAG_TASK_MIME` (kéo việc từ ngăn kéo lên lịch) có chủ đích: hai thao
 * tác cùng dùng HTML5 drag nhưng có đích và hậu quả khác hẳn. Chung một kiểu thì
 * thả nhầm chỗ sẽ **im lặng làm việc khác** — kéo xếp lại hàng mà rơi vào SVG sẽ
 * dời lịch của task.
 */
const REORDER_MIME = 'application/x-apm-gantt-row';

/**
 * Bề rộng mặc định của cột nhãn — APM-GANTT-i.
 *
 * Ba cột bên phải để **vừa đủ chứa nội dung của chúng**, không rộng hơn: tên
 * người là một dòng ngắn, số ngày là hai chữ số, và khoảng ngày là `dd/mm–dd/mm`.
 * Chỗ tiết kiệm được dồn hết cho cột tên việc — thứ duy nhất ở đây thật sự dài,
 * và cũng là thứ người dùng đọc trước.
 *
 * Sửa được từng cột (kéo mép), và khi tổng vượt bề rộng khung thì danh sách có
 * thanh cuộn ngang **của riêng nó** — không đẩy biểu đồ đi.
 */
const DEFAULT_COLUMNS = { task: 240, assignee: 96, status: 56, days: 32, dates: 84 } as const;

type ColumnKey = keyof typeof DEFAULT_COLUMNS;

/** Hẹp hơn ngần này thì tiêu đề cột không còn đọc được. */
const MIN_COLUMN_WIDTH = 28;

/** Hẹp hơn ngần này thì khung danh sách không còn thấy nổi cột "Việc". */
const MIN_LIST_PANEL_WIDTH = 160;

/**
 * Bề rộng khung danh sách ở điện thoại — chỉ còn cột tên việc.
 *
 * Bản rộng có năm cột, cộng lại ~530px: ở 390px nó chiếm hết bề ngang và vùng
 * biểu đồ (`flex-1 min-w-0`) co về **0px** — người dùng thấy một bảng việc và
 * không thấy thanh Gantt nào (báo 2026-10-07). Tên việc là thứ duy nhất cần để
 * biết hàng nào là việc gì; người làm, trạng thái, ngày đã có ở màu thanh và ở
 * chi tiết việc (bấm tên hoặc thanh).
 *
 * Tỉ lệ chứ không cố định: ~38% để biểu đồ luôn còn nhiều chỗ hơn danh sách,
 * kẹp hai đầu để máy 320px vẫn đọc được tên và máy tính bảng dọc không phí chỗ.
 */
const COMPACT_LIST_WIDTH = 'clamp(108px, 38%, 168px)';

/** Rung tay dưới ngần này pixel vẫn tính là một cú bấm, không phải kéo. */
const CLICK_SLOP_PX = 4;

const HANDLE_WIDTH = 6;

interface GanttChartProps {
  readonly view: GanttView;
  readonly zoom: Zoom;
  /**
   * Thời điểm đang xem — luật #7.
   *
   * Quyết định vạch "hôm nay" và cái gì bị coi là trễ. Dùng được ngay: mở Gantt
   * với `asOf` cuối tháng trước là có ngay ảnh chụp cho báo cáo tháng, không
   * phải chờ Replay ở Sprint 9.
   */
  readonly asOf?: Date;
  /**
   * Vạch mốc dọc — APM-TEST-h. Người dùng đầu tiên là vòng test (§9.3 mức 2).
   *
   * Biểu đồ không biết mốc là gì: nó vẽ vạch, nhãn, và gọi `onSelect` khi bấm.
   * Khung thời gian tự nới ra để bao mốc nằm ngoài vùng task — xem `widenWindow`.
   */
  readonly markers?: readonly GanttMarker[];
  /** Chỉ hiện task trên đường găng. */
  readonly criticalOnly?: boolean;
  /**
   * Bộ lọc/làm nổi dùng chung với Board và Danh sách — APM-BOARD-h.
   *
   * Chế độ `filter` cắt hàng đi; chế độ `highlight` giữ nguyên hàng và làm chìm
   * thanh không khớp. Chìm chứ không giấu là quan trọng ở đây hơn ở board: một
   * hàng biến mất khỏi Gantt cũng làm biến mất **mũi tên phụ thuộc** đi qua nó,
   * và một chuỗi phụ thuộc đứt quãng đọc thành một chuỗi không tồn tại.
   */
  readonly filter?: BoardFilter;
  readonly onReschedule: (taskId: string, range: DateRange) => void;
  readonly onSelect?: (taskId: string) => void;
  readonly selectedId?: string | null;
  /** Mở chi tiết task (bình luận / lịch sử / tệp) — bấm vào tên hoặc thanh. */
  readonly onOpen?: (taskId: string) => void;
  /** Nối hai task bằng cách kéo từ đầu nối bên phải sang thanh khác. */
  readonly onLink?: (fromTaskId: string, toTaskId: string) => void;
  /** Bấm vào mũi tên để bỏ liên kết. */
  readonly onUnlink?: (dependencyId: string) => void;
  /**
   * Thả một việc từ ngăn kéo lập lịch xuống biểu đồ — APM-BOARD-c.
   *
   * Trả lên **ngày** đã bắt dính vào ô, không phải toạ độ: trục thời gian là
   * chuyện của biểu đồ này. Bỏ trống ⇒ không nhận thả, và cú kéo rơi vào khoảng
   * không thay vì đặt lịch cho một việc ở đâu đó.
   */
  readonly onDropTask?: (taskId: string, startDate: string) => void;
  /** Mốc phải xong của dự án — vẽ thành vạch dọc có nhãn. */
  readonly deadline?: string | null;
  /**
   * Ảnh hưởng của lịch — APM-1312. `null` = chưa tải xong, Gantt vẫn vẽ bình thường.
   *
   * Prop rời chứ không nằm trong `view`: nó tới sau, và thiếu nó thì biểu đồ chỉ
   * mất màu cảnh báo — không mất thanh nào.
   */
  readonly impact?: ScheduleImpactView | null;
  /** Task đang được tích để làm tác vụ theo lô (gom nhóm) — APM-1306. */
  readonly selectedIds?: ReadonlySet<string>;
  readonly onToggleSelect?: (taskId: string, selected: boolean) => void;
  /** Đổi tên / xoá một nhóm, từ chính hàng tiêu đề của nhóm đó. */
  readonly onRenameGroup?: (groupId: string, name: string) => void;
  readonly onDeleteGroup?: (groupId: string) => void;
  /**
   * Kéo một hàng lên/xuống trong danh sách — APM-GANTT-h.
   *
   * `beforeTaskId = null` nghĩa là thả xuống cuối. Bỏ trống prop này thì hàng
   * không kéo được (người xem không có quyền sửa).
   */
  readonly onReorder?: (taskId: string, beforeTaskId: string | null) => void;
  /**
   * Cho phép **kéo thanh để dời lịch** — APM-GANTT-j.
   *
   * Tách khỏi `onReschedule` (vẫn bắt buộc) vì hai câu hỏi khác nhau: "gửi thay
   * đổi đi đâu" và "người đang xem có được sửa lúc này không". Mặc định `false`
   * — biểu đồ để **đọc**, và một cú trượt tay khi định bấm vào việc không dời
   * được lịch của nó.
   */
  readonly editable?: boolean;
}

/**
 * Một hàng của danh sách bên trái: tiêu đề nhóm, hoặc một task.
 *
 * `group: null` là hàng "chưa thuộc nhóm" — nó cần một hàng thật để phần chưa
 * xếp không trông như thuộc nhóm cuối cùng phía trên.
 */
type GanttRow =
  | { readonly kind: 'group'; readonly group: TaskGroupView | null }
  | { readonly kind: 'bar'; readonly bar: GanttBar };

/**
 * Gantt tự dựng bằng SVG — docs/01-tech-stack.md §7.
 *
 * Component **thuần trình bày**: nhận dữ liệu đã tính sẵn (đường găng, float,
 * slippage đều do server tính) và trả về ý định qua callback. Nhờ vậy Sprint 9
 * dùng lại nguyên vẹn nó cho Replay — chỉ cần đổi nguồn `view` và `asOf`.
 *
 * Không gõ phím ở đâu cả: kéo thân thanh để dời, kéo hai mép để co giãn, mọi
 * thứ bắt dính vào ô ngày (spec mục 13).
 */
export function GanttChart({
  view,
  zoom,
  asOf,
  markers = [],
  criticalOnly = false,
  filter = NO_BOARD_FILTER,
  onReschedule,
  onSelect,
  selectedId = null,
  onOpen,
  onLink,
  onUnlink,
  onDropTask,
  deadline = null,
  impact = null,
  selectedIds,
  onToggleSelect,
  onRenameGroup,
  onDeleteGroup,
  onReorder,
  editable = false,
}: GanttChartProps): ReactNode {
  const { t } = useTranslation();
  /** Điện thoại: danh sách chỉ còn cột tên việc — xem `COMPACT_LIST_WIDTH`. */
  const compact = useNarrowScreen();
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(480);
  // Cuộn ngang và bề rộng khung — chỉ để đặt dấu mép ở điện thoại (`compact`).
  const [scrollLeft, setScrollLeft] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [preview, setPreview] = useState<DateRange | null>(null);
  /**
   * Đang kéo một liên kết: task nguồn và vị trí con trỏ.
   *
   * Tách khỏi `drag` (kéo lịch) có chủ đích: hai thao tác bắt đầu từ hai chỗ
   * khác nhau trên cùng một thanh, và gộp state thì mỗi lần kéo phải hỏi "lần
   * này là dời lịch hay nối task" ở năm chỗ khác nhau.
   */
  const [linking, setLinking] = useState<{ fromTaskId: string; x: number; y: number } | null>(null);
  /**
   * Hạng mục con trỏ đang rê qua trong lúc nối — APM-GANTT-e.
   *
   * Trước đây **mọi** thanh khác đều sáng lên cùng lúc, nên người dùng không
   * biết mình đang chỉ vào cái nào — thứ duy nhất họ đọc được là "có thể thả ở
   * đâu đó". Sáng đúng một cái thì cú thả trở nên đoán trước được, đúng cách các
   * công cụ vẽ sơ đồ làm.
   */
  const [hoverTarget, setHoverTarget] = useState<string | null>(null);

  /**
   * Hàng mà hàng đang kéo sẽ được chèn **lên trên** — APM-GANTT-h.
   *
   * `'end'` = thả xuống cuối. Giữ ở state chứ không vẽ bằng CSS `:hover` vì
   * vạch chèn phải nằm ở **mép trên hoặc mép dưới** của một hàng tuỳ con trỏ
   * đang ở nửa nào — một luật mà CSS không diễn đạt được.
   */
  const [dropBefore, setDropBefore] = useState<string | 'end' | null>(null);

  /** Bề rộng từng cột của danh sách, kéo mép để đổi — APM-GANTT-i. */
  const [columns, setColumns] = useState<Record<ColumnKey, number>>({ ...DEFAULT_COLUMNS });
  const listWidth =
    columns.task + columns.assignee + columns.status + columns.days + columns.dates + 24;

  /**
   * Bề rộng cả khung danh sách, kéo ranh giới với biểu đồ để đổi — APM-GANTT-t.
   *
   * `null` = tự theo `listWidth` (tổng bốn cột), đúng hành vi trước khi có
   * ranh giới này. Khác `columns.*`: đây đổi **khung chứa**, không đổi cột nào
   * cả — kéo hẹp lại thì bốn cột vẫn giữ nguyên bề rộng và tự cuộn ngang bên
   * trong, chứ không bị bóp nhỏ theo.
   */
  const [listPanelWidth, setListPanelWidth] = useState<number | null>(null);
  const panelResizeFrom = useRef<{ x: number; width: number } | null>(null);

  /** Mốc vừa được kéo, đang chờ người dùng xác nhận — APM-GANTT-d. */
  const [confirmMove, setConfirmMove] = useState<{
    taskId: string;
    from: string;
    to: string;
  } | null>(null);

  /**
   * Đo chiều cao vùng biểu đồ, và **đo lại mỗi khi nó đổi** — APM-GANTT-o.
   *
   * ⚠️ Trước đây chỉ đo một lần lúc mount (`useLayoutEffect` với `[]`). Con số
   * ấy quyết định hai thứ: cửa sổ bên trái cao bao nhiêu, và `visibleRows` dựng
   * những hàng nào. Nên mọi thứ làm khung đổi chiều cao — đổi cỡ cửa sổ, gập
   * một nhóm, đổi bộ lọc, hay đơn giản là khung `max-h` co theo số hàng — đều
   * để lại một con số cũ: danh sách bên trái lệch chiều cao với biểu đồ, và
   * phần dôi ra bị cắt.
   *
   * Lỗi này ẩn cho tới khi khung cao lên (`70vh` → `100dvh-18rem`): với 480px
   * mặc định thì sai số nhỏ, còn với màn hình cao thì mất hẳn mấy hàng cuối.
   *
   * `ResizeObserver` chứ không `window.resize`: khung này co giãn vì nhiều lý
   * do không đi qua sự kiện resize của cửa sổ (mở/đóng thanh bên, hiện khối
   * "Nhờ AI xếp lại", số hàng thay đổi).
   */
  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const measure = (): void => {
      setViewportHeight(node.clientHeight);
      setViewportWidth(node.clientWidth);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  /**
   * Việc bị ảnh hưởng, tra theo id — APM-1312.
   *
   * Dựng lại mỗi render là chấp nhận được: danh sách có trần 20 phần tử, và một
   * `useMemo` ở đây chỉ thêm một phụ thuộc phải nhớ giữ đúng.
   */
  const impactedById = new Map<string, ScheduleImpactTask>(
    (impact?.tasks ?? []).map((task) => [task.taskId, task]),
  );

  /**
   * Cuộn ngang tới mốc "hôm nay" ở lần vẽ đầu — APM-1305.
   *
   * ⚠️ Không có bước này thì Gantt mở ra ở **đầu cửa sổ thời gian**, tức ngày
   * bắt đầu sớm nhất của dự án. Một dự án bảy tháng rộng 2.600px trong khung
   * 1.080px, nên người dùng thấy một phần ba đầu — và nếu công việc đang chạy
   * nằm ở tháng thứ năm thì họ thấy **một danh sách task bên trái với vùng
   * biểu đồ trắng trơn**, đúng như báo cáo. Bars có đủ; chúng ở ngoài khung.
   *
   * Lùi lại một phần tư khung để phần quá khứ gần đây cũng nhìn thấy được:
   * câu hỏi thường gặp không phải "hôm nay có gì" mà "vừa rồi trễ gì".
   */
  const scrolledOnce = useRef(false);
  useLayoutEffect(() => {
    const box = containerRef.current;
    if (!box || scrolledOnce.current || !view.windowStart || !view.windowEnd) return;

    const marker = createScale(parseDate(view.windowStart), parseDate(view.windowEnd), zoom);
    const target = dateToX(marker, asOf ?? new Date()) - box.clientWidth / 4;
    box.scrollLeft = Math.max(0, target);
    scrolledOnce.current = true;
  }, [view.windowStart, view.windowEnd, zoom, asOf]);

  /**
   * Nhóm đang gấp lại. Mặc định **mở hết**: một danh sách gấp sẵn thì người
   * dùng không thấy việc của mình ở đâu, và phải bấm mở từng nhóm mới biết.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  /*
    Hai bộ lọc chồng nhau, phép **và** — APM-BOARD-h.

    "Chỉ đường găng" là câu hỏi về *cấu trúc lịch*, còn thanh lọc chung là câu
    hỏi về *ai / loại gì / hạn nào*. Chúng độc lập, nên chọn cả hai nghĩa là
    "việc của Bảo **trên** đường găng" — đúng câu người ta muốn hỏi khi một dự
    án đang trượt.
  */
  /*
    ⚠️ Mốc hôm nay tính **tại chỗ**, không dùng `today` khai ở dưới: `const` là
    block-scoped nên đọc trước chỗ khai là lỗi lúc chạy (TDZ), và ở đây nó rơi
    đúng vào đường vẽ chính — trắng cả biểu đồ. Dời khai báo lên cũng được,
    nhưng nó đang đứng cạnh mấy thứ dùng nó nhiều hơn.
  */
  // Ngày của mốc theo múi giờ tổ chức — APM-TZ-i. `formatDate` cắt ngày UTC, nên
  // vạch "hôm nay" đứng ở hôm qua từ nửa đêm tới 7 giờ sáng giờ Việt Nam.
  const todayKey = orgToday(asOf ?? new Date());

  const byCritical = criticalOnly ? view.bars.filter((bar) => bar.critical) : view.bars;
  const filtered =
    filter.mode === 'filter' && isFilterActive(filter)
      ? byCritical.filter((bar) => matchesFilter(bar.task, filter, todayKey))
      : byCritical;

  /** Thanh không khớp ở chế độ "làm nổi" — làm chìm, giữ nguyên hàng. */
  const dimmedByFilter =
    filter.mode === 'highlight' && isFilterActive(filter)
      ? new Set(
          filtered
            .filter((bar) => !matchesFilter(bar.task, filter, todayKey))
            .map((bar) => bar.task.id),
        )
      : new Set<string>();

  /**
   * Task của nhóm đang gấp thì **không dựng hàng nào**.
   *
   * Gấp mà vẫn chiếm chỗ thì nó chỉ ẩn thanh, không thu gọn được danh sách —
   * mà thu gọn mới là lý do người ta gấp một nhóm mười lăm việc.
   */
  const bars = filtered.filter(
    (bar) => bar.task.groupId === null || !collapsed.has(bar.task.groupId),
  );

  /**
   * Hàng của danh sách: tiêu đề nhóm **chiếm một hàng thật** — APM-1306.
   *
   * ⚠️ Không thể vẽ tiêu đề nhóm như một nhãn lơ lửng: thanh trong SVG được
   * đặt theo **chỉ số hàng**, nên mỗi tiêu đề chèn thêm phải là một hàng ở cả
   * hai bên, nếu không cột nhãn và biểu đồ lệch nhau đúng bằng số nhóm.
   *
   * Server đã xếp task theo nhóm (`GanttService`), nên chỗ này chỉ cần chèn
   * tiêu đề mỗi khi `groupId` đổi.
   */
  const rows: GanttRow[] = [];
  let seen: string | null | undefined;
  for (const bar of bars) {
    const groupId = bar.task.groupId;
    if (groupId !== seen) {
      seen = groupId;
      const group = view.groups.find((entry) => entry.id === groupId);
      // Phần "chưa thuộc nhóm" chỉ có tiêu đề khi dự án **đã** có nhóm nào đó:
      // một dự án chưa dùng nhóm thì không cần một tiêu đề nói điều hiển nhiên.
      if (group) rows.push({ kind: 'group', group });
      else if (view.groups.length > 0) rows.push({ kind: 'group', group: null });
    }
    rows.push({ kind: 'bar', bar });
  }

  /**
   * Phạm vi ngày của **cả nhóm**, suy từ việc bên trong — APM-GANTT-f.
   *
   * ⚠️ Suy ra, không lưu. `task_group` cố ý **không có ngày** (CLAUDE.md: dùng
   * task cha làm "nhóm" là biến cái hộp thành một việc có ngày, có người, có
   * tiến độ, và nó chui vào burndown lẫn đường găng). Ngày của nhóm chỉ là hệ
   * quả của những việc trong nó, nên nó phải được tính lại mỗi lần vẽ chứ không
   * được cất ở đâu để rồi trôi khỏi sự thật.
   *
   * Việc chưa xếp lịch không tham gia: nó không có ngày nào để mà gộp.
   */
  const groupSpan = new Map<string, { start: Date; due: Date }>();
  for (const bar of bars) {
    const groupId = bar.task.groupId;
    if (!groupId || !bar.startDate || !bar.dueDate) continue;

    const start = parseDate(bar.startDate);
    const due = parseDate(bar.dueDate);
    const current = groupSpan.get(groupId);
    groupSpan.set(
      groupId,
      current
        ? {
            start: start < current.start ? start : current.start,
            due: due > current.due ? due : current.due,
          }
        : { start, due },
    );
  }

  /** Chỉ số hàng của từng task — mũi tên phụ thuộc vẽ theo nó. */
  const rowIndexOf = new Map<string, number>();
  rows.forEach((row, index) => {
    if (row.kind === 'bar') rowIndexOf.set(row.bar.task.id, index);
  });

  /**
   * Cú vừa rồi có phải **kéo** không — APM-GANTT-c.
   *
   * Trình duyệt phát `click` sau mỗi lượt `pointerdown` + `pointerup` trên cùng
   * một phần tử, **kể cả khi con trỏ đã đi một quãng dài ở giữa**. Thanh trên
   * Gantt vừa kéo được vừa bấm được, nên mỗi lần dời lịch hay nối phụ thuộc đều
   * kèm một cú click — và popup chi tiết task bật lên ngay sau khi người dùng
   * vừa thả tay.
   *
   * Là `ref` chứ không phải state: nó được đọc trong chính lượt xử lý sự kiện
   * ngay sau đó, mà một `setState` thì chưa kịp có hiệu lực ở đó.
   */
  const dragged = useRef(false);

  /*
    ⚠️ Mọi hook của component phải đứng **trên** lệnh `return` sớm ngay dưới đây.

    `dragged` từng nằm sau nó. Dự án chưa có việc nào có ngày thì component thoát
    ở `return` ấy với ít hơn một hook; chuyển sang dự án có ngày (vẫn ở chế độ
    Gantt, nên vẫn là **cùng một** instance) là React thấy số hook tăng lên và
    ném "Rendered more hooks than during the previous render" — cả trang thành
    "Something went wrong" (production 2026-10-08). Repo chưa có lint kiểm quy
    tắc hook, nên thứ canh chỗ này là test "đổi từ dự án không ngày sang có ngày".
  */
  if (!view.windowStart || !view.windowEnd) {
    return <p className="py-10 text-center text-sm text-muted-foreground">{t('gantt.noDates')}</p>;
  }

  /*
    Khung nới theo mốc **trước** khi dựng trục: vòng test cuối thường diễn ra
    sau khi mọi task đã hết hạn, nên nó luôn nằm ngoài `windowEnd` do server
    suy từ ngày task. Không nới thì vạch bị vẽ ra ngoài vùng cuộn và biểu đồ
    trông như thiếu vạch chứ không như một lỗi.
  */
  const markerDates = markers.map((marker) => parseDate(marker.date));
  const span = widenWindow(parseDate(view.windowStart), parseDate(view.windowEnd), markerDates);
  const scale = createScale(span.start, span.end, zoom);
  const today = asOf ?? new Date();

  function beginDrag(bar: GanttBar, mode: DragMode, event: React.PointerEvent): void {
    if (!bar.startDate || !bar.dueDate) return;
    // Bắt con trỏ để thanh vẫn theo tay khi chuột ra ngoài SVG. Không phải môi
    // trường nào cũng có API này, và thiếu nó thì kéo vẫn chạy — chỉ kém mượt
    // hơn — nên đừng để nó làm hỏng cả thao tác.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.stopPropagation();
    dragged.current = false;

    setDrag({
      taskId: bar.task.id,
      mode,
      originX: event.clientX,
      startDate: bar.startDate,
      dueDate: bar.dueDate,
    });
    setPreview({ startDate: bar.startDate, dueDate: bar.dueDate });
  }

  /**
   * Toạ độ con trỏ trong hệ của `<svg>` — APM-GANTT-e.
   *
   * ⚠️ **Một** hàm cho mọi chỗ. Trước đây lúc bấm thì cộng thêm 40 vào `y`, lúc
   * vẽ thì trừ đi 40, còn lúc di chuyển thì không làm gì cả — nên đường kẻ đứng
   * đúng chỗ ở khoảnh khắc đầu rồi lệch 40px suốt phần còn lại của thao tác.
   * Đó là toàn bộ lý do việc nối phụ thuộc "không mượt".
   *
   * `<svg>` khai `width`/`height` bằng pixel và **không** có `viewBox`, nên một
   * đơn vị SVG là một pixel CSS và phép trừ này là đủ.
   */
  function svgPoint(
    event: React.PointerEvent,
    svg: SVGSVGElement | null,
  ): { x: number; y: number } {
    const box = svg?.getBoundingClientRect();
    if (!box) return { x: 0, y: 0 };
    return { x: event.clientX - box.left, y: event.clientY - box.top };
  }

  function moveDrag(event: React.PointerEvent): void {
    if (linking) {
      // Đang nối thì mọi cú thả đều là thao tác nối, không phải bấm.
      dragged.current = true;
      setLinking({
        ...linking,
        ...svgPoint(event, event.currentTarget as unknown as SVGSVGElement),
      });
      return;
    }
    if (!drag) return;

    /*
      Ngưỡng vài pixel, không phải "có di chuyển hay không": tay người rung vài
      pixel trong lúc bấm là chuyện bình thường, và coi đó là kéo sẽ **nuốt mất
      cú bấm** — lỗi khó chịu hơn hẳn lỗi đang sửa, vì nó làm thanh trông như
      không bấm được.
    */
    if (Math.abs(event.clientX - drag.originX) > CLICK_SLOP_PX) dragged.current = true;
    setPreview(applyDrag(drag, scale, event.clientX - drag.originX));
  }

  function endDrag(): void {
    if (drag && preview && hasMoved(drag, preview)) {
      const moved = view.bars.find((bar) => bar.task.id === drag.taskId);

      /*
        Mốc thì **hỏi lại** — APM-GANTT-d.

        Kéo một thanh việc là chuyện xảy ra hàng chục lần trong một buổi lập kế
        hoạch, sai thì kéo lại. Một mốc là ngày đã cam kết với bên ngoài (bàn
        giao, nghiệm thu), nên một cú trượt tay vài pixel không nên dời được nó
        trong im lặng.
      */
      if (moved && moved.kind !== 'task') {
        setConfirmMove({ taskId: drag.taskId, from: drag.dueDate, to: preview.dueDate });
        setDrag(null);
        // ⚠️ Giữ `preview`: hình thoi phải ở lại chỗ mới trong lúc hộp thoại
        // mở, nếu không người dùng đọc "dời sang ngày X" trong khi nhìn thấy nó
        // vẫn nằm ở ngày cũ.
        return;
      }

      onReschedule(drag.taskId, preview);
    }
    setDrag(null);
    setPreview(null);
  }

  /** Thả liên kết lên một thanh khác ⇒ tạo phụ thuộc "xong-thì-mới-bắt-đầu". */
  function dropLink(toTaskId: string): void {
    if (!linking) return;
    // Nối task với chính nó là vòng lặp; server cũng từ chối, nhưng chặn ở đây
    // thì người dùng không phải đọc một thông báo lỗi cho một cú thả sai.
    if (linking.fromTaskId !== toTaskId) onLink?.(linking.fromTaskId, toTaskId);
    setLinking(null);
    setHoverTarget(null);
  }

  /** Ngày đang hiển thị: bản xem trước khi đang kéo, dữ liệu thật khi không. */
  function rangeOf(bar: GanttBar): { start: Date; due: Date } | null {
    const source = drag?.taskId === bar.task.id && preview ? preview : bar;
    if (!source.startDate || !source.dueDate) return null;
    return { start: parseDate(source.startDate), due: parseDate(source.dueDate) };
  }

  const rowsHeight = rows.length * ROW_HEIGHT;
  const { from, to } = visibleRows(scrollTop, viewportHeight, ROW_HEIGHT, rows.length);

  /**
   * Dấu mép cho thanh nằm **ngoài khung nhìn ngang** — chỉ ở điện thoại.
   *
   * Khung biểu đồ ở 390px chỉ rộng ~230px, tức ba tuần ở mức Tuần. Phần lớn
   * hàng vì thế trống trơn, và một hàng trống đọc như "việc này chưa có lịch".
   * Dấu "‹ 12/06" ở mép trái nói thanh đã qua và kết thúc ngày nào; "03/11 ›" ở
   * mép phải nói nó chưa tới. Bấm thì cuộn tới thanh — khỏi vuốt mò.
   *
   * DOM chứ không SVG: nó bám theo `scrollLeft`, tức đứng yên trên màn hình
   * khi biểu đồ trôi dưới nó — cùng lý do dải nhãn thời gian là DOM.
   */
  function renderEdgeHints(): ReactNode {
    if (viewportWidth === 0) return null;
    const right = scrollLeft + viewportWidth;
    return rows.slice(from, to).map((row, offset) => {
      if (row.kind !== 'bar') return null;
      const bar = row.bar;
      const range = rangeOf(bar);
      if (!range) return null;
      const x0 = dateToX(scale, range.start);
      const x1 = dateToX(scale, addDays(range.due, 1));
      const side = x1 <= scrollLeft + 2 ? 'left' : x0 >= right - 2 ? 'right' : null;
      if (side === null) return null;

      const health = scheduleHealth(bar, today);
      const date = shortDate(formatDate(side === 'left' ? range.due : range.start));
      return (
        <button
          key={bar.task.id}
          type="button"
          aria-label={t('gantt.offscreenHint', { name: bar.task.title })}
          onClick={() =>
            containerRef.current?.scrollTo({
              left: Math.max(0, x0 - viewportWidth / 4),
              behavior: 'smooth',
            })
          }
          style={{
            top: HEADER_HEIGHT + (from + offset) * ROW_HEIGHT + (ROW_HEIGHT - 18) / 2,
            left: side === 'left' ? scrollLeft + 3 : right - 3,
          }}
          className={cn(
            'absolute z-[5] flex h-[18px] items-center rounded-full border border-border bg-background/90 px-1.5 text-[10px] tabular-nums shadow-sm',
            side === 'right' && '-translate-x-full',
            health === 'overdue' || health === 'doneLate'
              ? 'font-semibold text-rag-red'
              : 'text-muted-foreground',
          )}
        >
          {side === 'left' ? `‹ ${date}` : `${date} ›`}
        </button>
      );
    });
  }

  /**
   * Hàng **task** ngay sau hàng này, để "thả vào nửa dưới" biết chèn trước ai.
   *
   * Bỏ qua hàng tiêu đề nhóm: chúng không phải task nên không có id để chèn
   * trước, và thả xuống dưới hàng cuối của một nhóm nghĩa là xuống cuối danh
   * sách (`'end'`), không phải chèn vào giữa nhóm sau.
   */
  function nextRowId(taskId: string): string | 'end' {
    const index = rows.findIndex((row) => row.kind === 'bar' && row.bar.task.id === taskId);
    const next = rows[index + 1];
    return next?.kind === 'bar' ? next.bar.task.id : 'end';
  }

  /** Một hàng task ở cột nhãn. Tách ra vì hàng nhóm và hàng task xen nhau. */
  function renderTaskRow(bar: GanttBar): ReactNode {
    const health = scheduleHealth(bar, today);
    const lateTitle = doneLateTitle(t, bar);
    return (
      <div
        key={bar.task.id}
        style={{ height: ROW_HEIGHT }}
        {...(onReorder
          ? {
              draggable: true,
              onDragStart: (event: React.DragEvent) => {
                event.dataTransfer.setData(REORDER_MIME, bar.task.id);
                event.dataTransfer.effectAllowed = 'move';
              },
              onDragOver: (event: React.DragEvent) => {
                /*
                  `preventDefault` là **bắt buộc**: thiếu nó thì trình duyệt coi
                  đây không phải vùng thả và `drop` không bao giờ chạy — mà không
                  có lỗi nào để lần theo (cùng bẫy đã ghi ở vùng thả của SVG).
                */
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';

                // Nửa trên = chèn lên trước hàng này; nửa dưới = chèn xuống sau.
                const box = event.currentTarget.getBoundingClientRect();
                const below = event.clientY - box.top > box.height / 2;
                setDropBefore(below ? nextRowId(bar.task.id) : bar.task.id);
              },
              onDragLeave: () => setDropBefore(null),
              onDrop: (event: React.DragEvent) => {
                event.preventDefault();
                const dragged = event.dataTransfer.getData(REORDER_MIME);
                const target = dropBefore;
                setDropBefore(null);
                if (!dragged || target === null || dragged === target) return;
                onReorder(dragged, target === 'end' ? null : target);
              },
              onDragEnd: () => setDropBefore(null),
            }
          : {})}
        className={cn(
          'group/row flex items-center gap-2 border-b border-border/50 px-2 text-xs',
          onReorder && 'cursor-grab',
          selectedId === bar.task.id && 'bg-muted',
          linking?.fromTaskId === bar.task.id && 'bg-primary/10',
          // Vạch chèn: mép trên của hàng sắp bị đẩy xuống.
          dropBefore === bar.task.id && 'border-t-2 border-t-primary',
          // Hàng bên trái chìm cùng thanh bên phải — hai nửa của **một** hàng,
          // và làm nhạt một nửa trông như lỗi vẽ chứ không như một bộ lọc.
          dimmedByFilter.has(bar.task.id) && 'opacity-35',
        )}
      >
        {/* Gom nhóm theo lô là việc sắp xếp kế hoạch, làm ở màn rộng. */}
        {onToggleSelect && !compact ? (
          <input
            type="checkbox"
            checked={selectedIds?.has(bar.task.id) ?? false}
            onChange={(event) => onToggleSelect(bar.task.id, event.target.checked)}
            aria-label={t('gantt.selectTask', { name: bar.task.title })}
            className="ios-check ios-check-select"
          />
        ) : null}

        <span
          style={compact ? undefined : { width: columns.task }}
          className={cn('flex min-w-0 items-center gap-1', compact ? 'flex-1' : 'shrink-0')}
        >
          {bar.kind === 'task' ? null : (
            <span className="shrink-0 text-foreground" title={t(`gantt.kind.${bar.kind}`)}>
              ◆
            </span>
          )}
          {bar.critical ? (
            <span className="shrink-0 text-rag-red" title={t('gantt.criticalTitle')}>
              ▲
            </span>
          ) : null}
          {/*
            Lịch sai cũng có dấu ở **danh sách**, không chỉ trên thanh —
            APM-GANTT-o.

            Hai lý do. Một: thanh của việc ấy có thể đang nằm ngoài khung nhìn
            ngang, và lúc đó hoa văn không giúp được gì — nhưng tên việc thì
            luôn thấy. Hai: đây là danh sách người ta quét mắt từ trên xuống để
            tìm chỗ phải sửa, và một dấu trên đúng dòng ấy tìm nhanh hơn hẳn
            việc dò từng thanh.
          */}
          {bar.violationDays > 0 ? (
            <span
              className="shrink-0 text-rag-red"
              title={t('gantt.violationTitle', { days: bar.violationDays })}
            >
              ⚠
            </span>
          ) : null}
          {/* Bấm tên mở chi tiết — cùng hành vi với bấm thanh. */}
          <button
            type="button"
            onClick={() => {
              onSelect?.(bar.task.id);
              onOpen?.(bar.task.id);
            }}
            title={compact ? bar.task.title : undefined}
            className={cn(
              'min-w-0 text-left hover:underline',
              // Điện thoại: hai dòng chữ nhỏ vừa khít hàng 28px. Một dòng ở
              // ~130px thì mười việc "Mô hình dữ liệu chung: …" cắt thành mười
              // dòng giống hệt nhau — phần phân biệt chúng nằm sau dấu hai chấm.
              compact ? 'line-clamp-2 break-words text-[11px] leading-[13px]' : 'truncate',
            )}
          >
            {bar.task.title}
          </button>

          {/*
            Việc **ngoài kế hoạch PM** — APM-TASK-creator.

            Chấm nhỏ chứ không phải chữ: hàng này đã chật và tên việc mới là thứ
            người ta đọc. Chỉ khi `=== false`; `null` là *chưa biết* (Replay,
            bản nháp) và không được dán dấu.
          */}
          {isOffPlan(bar.task) ? (
            <span
              aria-label={t('board.createdByOtherUnknown')}
              title={
                bar.task.createdByName === null
                  ? t('board.createdByOtherUnknown')
                  : t('board.createdByOtherTitle', { name: bar.task.createdByName })
              }
              className="ml-1 inline-block size-1.5 shrink-0 rounded-full bg-rag-amber align-middle"
            />
          ) : null}
          {/*
            Ô SP ở cuối ô tên — người dùng yêu cầu 2026-11-02: luôn hiện, bấm xem nguồn
            (so với việc cũ hay tự điền) và ước lượng lại. Mốc / cổng pha không có SP.
          */}
          {bar.kind === 'task' ? <StoryPointBadge task={bar.task} className="ml-auto" /> : null}
        </span>

        {compact ? null : (
          <span
            style={{ width: columns.assignee }}
            className="shrink-0 truncate text-muted-foreground"
          >
            {bar.task.assigneeName ?? t('gantt.noAssignee')}
          </span>
        )}

        {/*
          Trạng thái — chưa làm thì "–" (không có gì để đo), đang làm thì %
          tiến độ (`progressPct` của task cha là roll-up từ subtask, không
          nhập tay — spec 9.1), xong thì một dấu tích thay vì "100%": xong là
          một trạng thái, không phải một con số đang tiến tới đâu đó nữa.
        */}
        {compact ? null : (
          <span
            style={{ width: columns.status }}
            className="flex shrink-0 items-center justify-center tabular-nums text-muted-foreground"
          >
            {bar.task.statusCategory === 'done' ? (
              <CheckCircle2 aria-label={t('gantt.statusDone')} className="size-4 text-rag-green" />
            ) : bar.task.statusCategory === 'in_progress' ? (
              `${bar.task.progressPct}%`
            ) : (
              <span aria-label={t('gantt.statusTodo')}>–</span>
            )}
          </span>
        )}

        {/* Số ngày: khoảng đóng `[start, due]`, nên cùng ngày là một ngày
                    công. Mốc để trống — một cái mốc dài 0 ngày. */}
        {compact ? null : (
          <span
            style={{ width: columns.days }}
            className="shrink-0 text-right tabular-nums text-muted-foreground"
          >
            {spanDays(bar)}
          </span>
        )}

        {/*
          Mốc hiện **một** ngày, không phải một khoảng — APM-GANTT-e.

          Mốc có thời lượng bằng 0 (`startDate === dueDate`), nên "09/09–09/09"
          vừa thừa vừa mâu thuẫn với chính hình thoi đứng cạnh nó: một hình
          không có bề rộng mà lại có hai đầu mốc thời gian.
        */}
        {compact ? null : (
          <span
            style={{ width: columns.dates }}
            className={cn(
              'shrink-0 text-right tabular-nums',
              // Trễ hạn — chưa xong hay đã xong trễ — thì khoảng ngày đỏ đậm, cùng
              // kiểu với hạn trên thẻ board và ở bảng Danh sách.
              health === 'overdue' || health === 'doneLate'
                ? 'font-semibold text-rag-red'
                : 'text-muted-foreground',
            )}
            {...(lateTitle ? { title: lateTitle } : {})}
          >
            {barPeriod(bar) ?? t('gantt.noDatesShort')}
          </span>
        )}
      </div>
    );
  }

  return (
    /**
     * Hai vùng cuộn ngang, **một** vùng cuộn dọc — APM-GANTT-i.
     *
     * ## Vì sao tách cuộn ngang
     *
     * Bản trước dùng một khung cuộn cho cả hai bên, với cột nhãn `sticky
     * left-0`. Nó giữ hai nửa của một hàng luôn khớp, nhưng kéo rộng một cột
     * của danh sách là đẩy cả biểu đồ sang phải — hai bảng không liên quan gì
     * nhau lại dùng chung một thanh cuộn.
     *
     * Nay mỗi bên có thanh cuộn ngang riêng: kéo cột chỉ ảnh hưởng danh sách,
     * cuộn sang tháng sau chỉ ảnh hưởng biểu đồ.
     *
     * ## ⚠️ Cuộn dọc vẫn phải là MỘT
     *
     * Đây chính là chỗ bản-trước-nữa đã hỏng: hai vùng cuộn dọc riêng thì trang
     * hiện hai thanh cuộn, và cuộn cái này thì cái kia đứng yên — hai nửa của
     * cùng một hàng lệch nhau.
     *
     * Nên chỉ **vùng biểu đồ** cuộn dọc thật; danh sách bị dịch bằng
     * `translateY` theo đúng `scrollTop` ấy. Một nguồn, không có gì để lệch.
     */
    /*
      Chiều cao: `70vh` trước đây bỏ phí gần một phần ba màn hình vì bên dưới
      còn thanh chi tiết (đã bỏ ở APM-GANTT-k). Nay lấy nốt chỗ ấy.

      Trừ đi `18rem` là phần khung cố định phía trên (header trang, tên dự án,
      hàng tab, thanh công cụ của Gantt) — đo trên bố cục hiện tại. Vẫn là
      `max-h` chứ không phải `h`: dự án ít việc thì khung co lại theo nội dung,
      không để một mảng viền trống chiếm hết màn hình.

      `dvh` chứ không `vh`: trên trình duyệt di động `vh` tính theo khung lúc
      thanh địa chỉ **đã thu**, nên với thanh địa chỉ đang hiện thì đáy biểu đồ
      nằm dưới mép màn hình và không cuộn tới được.
    */
    <div className="flex max-h-[calc(100dvh-18rem)] min-h-64 rounded-DEFAULT border border-border">
      {/* Cột nhãn: bảng công việc, có thanh cuộn ngang của riêng nó. */}
      {/*
        `overflow-y-hidden` khai **tường minh**: CSS quy định khi một trục khác
        `visible` thì trục kia tự thành `auto`, nên chỉ viết `overflow-x-auto`
        là vô tình bật luôn thanh cuộn dọc ở đây — đúng thứ mà chú thích "cuộn
        dọc phải là MỘT" ở trên tồn tại để ngăn.
      */}
      <div
        className="shrink-0 overflow-x-auto overflow-y-hidden bg-card"
        /*
          ⚠️ `listWidth` chỉ là tổng bề rộng **cột** cộng một hằng số ước lượng
          cho gap/đệm — không khớp pixel với bề rộng thật của hàng (còn tuỳ
          `gap-2`/`px-2` và có checkbox chọn hàng hay không). Đặt nó làm `width`
          **luôn** thì phần chênh lệch hiện ra thành một dải nền trống sau cột
          cuối — đúng dải trắng vừa bị báo, ở cột "Từ – đến".

          Nên chỉ ép `width` khi người dùng đã tự kéo ranh giới
          (`listPanelWidth !== null`); mặc định để trống, khung tự khớp đúng
          bề rộng nội dung thật như trước khi có ranh giới này.
        */
        style={
          compact
            ? { width: COMPACT_LIST_WIDTH }
            : listPanelWidth === null
              ? undefined
              : { width: listPanelWidth }
        }
      >
        <div style={compact ? undefined : { minWidth: listWidth }}>
          {/*
            `select-none`: kéo mép cột mà con trỏ đi ngang qua chữ thì trình
            duyệt **bôi đen** tiêu đề thay vì đổi bề rộng — thao tác trông như
            hỏng, và vệt xanh còn ở lại sau khi thả tay.
          */}
          <div className="flex h-10 select-none items-center gap-2 border-b border-border bg-muted px-2 text-[11px] font-medium text-muted-foreground">
            {compact ? (
              <span className="truncate">{t('gantt.colTask')}</span>
            ) : (
              <>
                <ColumnHeader
                  label={t('gantt.colTask')}
                  width={columns.task}
                  onResize={(width) => setColumns((all) => ({ ...all, task: width }))}
                />
                <ColumnHeader
                  label={t('gantt.colAssignee')}
                  width={columns.assignee}
                  onResize={(width) => setColumns((all) => ({ ...all, assignee: width }))}
                />
                <ColumnHeader
                  label={t('gantt.colStatus')}
                  width={columns.status}
                  onResize={(width) => setColumns((all) => ({ ...all, status: width }))}
                />
                <ColumnHeader
                  label={t('gantt.colDays')}
                  width={columns.days}
                  onResize={(width) => setColumns((all) => ({ ...all, days: width }))}
                />
                <ColumnHeader
                  label={t('gantt.colDates')}
                  width={columns.dates}
                  onResize={(width) => setColumns((all) => ({ ...all, dates: width }))}
                />
              </>
            )}
          </div>

          {/*
            Cửa sổ cao đúng bằng vùng biểu đồ, nội dung dịch theo `scrollTop`
            của nó. `overflow-hidden` chứ không `auto`: thanh cuộn dọc thứ hai
            là đúng thứ bản trước đã hỏng vì nó.
          */}
          <div
            style={{ height: Math.max(0, viewportHeight - HEADER_HEIGHT) }}
            className="overflow-hidden"
          >
            <div style={{ transform: `translateY(${-scrollTop}px)` }}>
              {rows.map((row) =>
                row.kind === 'group' ? (
                  <GroupHeaderRow
                    key={`group-${row.group?.id ?? 'none'}`}
                    group={row.group}
                    collapsed={row.group !== null && collapsed.has(row.group.id)}
                    onToggle={() =>
                      row.group === null
                        ? undefined
                        : setCollapsed((current) => {
                            const next = new Set(current);
                            if (next.has(row.group!.id)) next.delete(row.group!.id);
                            else next.add(row.group!.id);
                            return next;
                          })
                    }
                    {...(onRenameGroup ? { onRename: onRenameGroup } : {})}
                    {...(onDeleteGroup ? { onDelete: onDeleteGroup } : {})}
                  />
                ) : (
                  renderTaskRow(row.bar)
                ),
              )}
            </div>
          </div>
        </div>
      </div>

      {/*
        Ranh giới kéo được giữa danh sách và biểu đồ — APM-GANTT-t. Cùng cơ chế
        pointer + `setPointerCapture` với `ColumnHeader`, nhưng đổi
        `listPanelWidth` (khung chứa) chứ không đổi cột nào — khung hẹp lại
        thì nội dung bốn cột tự cuộn ngang bên trong nó thay vì bị bóp nhỏ.
      */}
      {/* Điện thoại: khung danh sách đã cố định theo tỉ lệ, không kéo đổi. */}
      {compact ? (
        <div aria-hidden className="w-px shrink-0 bg-border" />
      ) : (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t('gantt.resizeList')}
          onPointerDown={(event) => {
            event.preventDefault();
            panelResizeFrom.current = { x: event.clientX, width: listPanelWidth ?? listWidth };
            event.currentTarget.setPointerCapture?.(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (!panelResizeFrom.current) return;
            const next =
              panelResizeFrom.current.width + (event.clientX - panelResizeFrom.current.x);
            setListPanelWidth(Math.max(MIN_LIST_PANEL_WIDTH, Math.round(next)));
          }}
          onPointerUp={() => {
            panelResizeFrom.current = null;
          }}
          className="w-1.5 shrink-0 cursor-col-resize border-x border-border bg-muted/50 hover:bg-primary/40"
        />
      )}

      {/* Vùng biểu đồ: cuộn cả hai chiều, và là **nguồn** của cuộn dọc. */}
      <div
        ref={containerRef}
        onScroll={(event) => {
          setScrollTop(event.currentTarget.scrollTop);
          if (compact) setScrollLeft(event.currentTarget.scrollLeft);
        }}
        className="min-w-0 flex-1 overflow-auto"
      >
        <div style={{ width: scale.width }} className="relative">
          {/*
            Dải nhãn thời gian là **DOM**, không phải `<text>` trong SVG: chỉ
            phần tử DOM mới `sticky` được, và nhãn thời gian trôi mất khi cuộn
            dọc là mất luôn khả năng đọc biểu đồ ở hàng thứ ba mươi.
          */}
          <div className="sticky top-0 z-10 h-10 border-b border-border bg-muted">
            {ticks(scale, zoom).map((tick) => (
              <span
                key={tick.x}
                style={{ left: tick.x + 2 }}
                className={cn(
                  'absolute top-2 text-[10px]',
                  tick.major ? 'font-medium text-foreground' : 'text-muted-foreground',
                )}
              >
                {tick.label}
              </span>
            ))}

            {/*
              Nhãn mốc nằm ở dải **DOM** này, không phải trong SVG — APM-TEST-h.

              Hai lý do, và lý do thứ hai mới là lý do thật:

              1. Dải này `sticky`, nên nhãn còn đọc được ở hàng thứ ba mươi.
              2. Chỗ bấm phải nằm **trên** các thanh. Mốc được vẽ trước thanh
                 (để thanh không bị vạch cắt qua mặt), và một `<line>` dày 2px
                 vốn đã là chỗ bấm gần như không trúng — cùng cái bẫy mà
                 `LinkHandle` đã gặp với mũi tên phụ thuộc.
            */}
            {markers.map((marker) =>
              marker.onSelect ? (
                <button
                  key={marker.key}
                  type="button"
                  onClick={marker.onSelect}
                  style={{ left: dateToX(scale, parseDate(marker.date)) + 2 }}
                  title={marker.title ?? marker.label}
                  className="absolute bottom-0.5 rounded-sm border border-rag-unknown bg-background px-1 text-[10px] font-medium text-foreground hover:bg-muted"
                >
                  {marker.label}
                </button>
              ) : (
                <span
                  key={marker.key}
                  style={{ left: dateToX(scale, parseDate(marker.date)) + 2 }}
                  title={marker.title ?? marker.label}
                  className="absolute bottom-0.5 rounded-sm border border-rag-unknown bg-background px-1 text-[10px] font-medium text-muted-foreground"
                >
                  {marker.label}
                </span>
              ),
            )}
          </div>

          {/*
            ⚠️ Kéo thả **của trình duyệt**, tách hẳn khỏi pointer event mà thanh
            Gantt đang dùng. Hai cơ chế không giành nhau: `dragover`/`drop` chỉ
            phát khi cú kéo bắt đầu từ một phần tử `draggable`, tức từ ngăn kéo
            lập lịch chứ không phải từ một thanh.

            `preventDefault` ở `dragover` là **bắt buộc** — thiếu nó thì trình
            duyệt coi đây không phải vùng thả và `drop` không bao giờ chạy, mà
            không có lỗi nào để lần theo.
          */}
          <svg
            width={scale.width}
            height={rowsHeight}
            {...(onDropTask
              ? {
                  onDragOver: (event: React.DragEvent) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                  },
                  onDrop: (event: React.DragEvent) => {
                    event.preventDefault();
                    const taskId = event.dataTransfer.getData(DRAG_TASK_MIME);
                    if (!taskId) return;
                    const box = event.currentTarget.getBoundingClientRect();
                    onDropTask(taskId, formatDate(xToDate(scale, event.clientX - box.left)));
                  },
                }
              : {})}
            onPointerMove={moveDrag}
            onPointerUp={() => {
              endDrag();
              setLinking(null);
              setHoverTarget(null);
            }}
            onPointerCancel={() => {
              endDrag();
              setLinking(null);
              setHoverTarget(null);
            }}
            role="img"
            aria-label={t('gantt.chartLabel')}
          >
            {weekendBands(scale).map((band) => (
              <rect
                key={band.x}
                x={band.x}
                y={0}
                width={band.width}
                height={rowsHeight}
                className="fill-muted/40"
              />
            ))}

            {/*
              Ngày nghỉ lễ vẽ **sọc nền** trong SVG, không tô thanh việc — APM-1312.

              ⚠️ Phải nằm **trong** `<svg>`. Đặt nó ở dải nhãn (DOM) phía trên
              thì `<rect>` không vẽ ra gì, và tệ hơn: `<title>` trong ngữ cảnh
              HTML là thẻ tiêu đề trang — Chrome đổi luôn tên tab thành "Nghỉ lễ:
              …". Đã cắn đúng một lần.

              Vì sao là sọc nền: ngày lễ trừ giờ của cả đội như nhau, nên tô thanh
              theo nó là tô cả biểu đồ. Một sọc trả lời đúng câu người xem hỏi khi
              thấy tuần đó trống.
            */}
            {(impact?.holidays ?? []).map((holiday) => {
              const x = dateToX(scale, parseDate(holiday.date));
              const width = dateToX(scale, addDays(parseDate(holiday.date), 1)) - x;
              return (
                <rect
                  key={holiday.id}
                  x={x}
                  y={0}
                  width={width}
                  height={rowsHeight}
                  className="fill-rag-amber/20"
                >
                  <title>{t('gantt.holidayTitle', { name: holiday.name })}</title>
                </rect>
              );
            })}

            {ticks(scale, zoom).map((tick) => (
              <line
                key={tick.x}
                x1={tick.x}
                y1={0}
                x2={tick.x}
                y2={rowsHeight}
                className={tick.major ? 'stroke-border' : 'stroke-border/40'}
              />
            ))}

            {/*
              Vạch mốc — APM-TEST-h. Vẽ **trước** vạch "hôm nay" và trước các
              thanh: mốc là nền, không phải nội dung.

              ⚠️ Nét **liền**, không `strokeDasharray`. `gantt-chart.spec.tsx`
              tìm vạch "hôm nay" bằng `line[stroke-dasharray]`, nên một vạch nét
              đứt thêm vào đây sẽ khiến test ấy âm thầm đo sang vạch của mốc —
              nó vẫn xanh, chỉ là đang canh thứ khác.

              `pointer-events-none`: chỗ bấm là cái nhãn ở dải sticky phía trên.
            */}
            {markers.map((marker) => (
              <line
                key={marker.key}
                x1={dateToX(scale, parseDate(marker.date))}
                y1={0}
                x2={dateToX(scale, parseDate(marker.date))}
                y2={rowsHeight}
                className="pointer-events-none stroke-rag-unknown"
                strokeWidth={1.5}
              />
            ))}

            {/* Vạch "hôm nay" theo `asOf`, không theo đồng hồ hệ thống. */}
            <line
              x1={dateToX(scale, today)}
              y1={0}
              x2={dateToX(scale, today)}
              y2={rowsHeight}
              className="stroke-rag-amber"
              strokeWidth={2}
              strokeDasharray="4 3"
            />

            {/*
              Mốc phải xong của dự án. Vẽ riêng khỏi vạch hôm nay và tô đỏ: đây
              là ngày mà mọi thanh nằm bên phải nó đều là một lời hứa đã vỡ, và
              không có vạch này thì người xem phải tự nhớ ngày đó trong đầu.
            */}
            {deadline ? (
              <g>
                <line
                  x1={dateToX(scale, parseDate(deadline))}
                  y1={0}
                  x2={dateToX(scale, parseDate(deadline))}
                  y2={rowsHeight}
                  className="stroke-rag-red"
                  strokeWidth={2}
                />
                <text
                  x={dateToX(scale, parseDate(deadline)) + 4}
                  y={12}
                  className="fill-rag-red text-[10px] font-medium"
                >
                  {t('gantt.deadlineLabel')}
                </text>
              </g>
            ) : null}

            {view.links.map((link) => (
              <DependencyArrow
                key={link.id}
                link={link}
                bars={bars}
                rowIndex={rowIndexOf}
                scale={scale}
              />
            ))}

            {/* Đường kéo đang theo con trỏ, chỉ hiện trong lúc nối. */}
            {linking ? (
              <line
                x1={linkOrigin(linking.fromTaskId)?.x ?? linking.x}
                y1={linkOrigin(linking.fromTaskId)?.y ?? linking.y}
                x2={linking.x}
                y2={linking.y}
                className="pointer-events-none stroke-primary"
                strokeWidth={2}
                strokeDasharray="3 2"
              />
            ) : null}

            {/*
              Đi theo `rows`, không theo `bars`: chỉ số hàng phải khớp cột nhãn,
              mà cột nhãn có thêm hàng tiêu đề nhóm. Hàng tiêu đề không vẽ gì —
              nhóm là một cái hộp có tên, không phải một việc có thời gian.
            */}
            {rows.slice(from, to).map((row, offset) => {
              const index = from + offset;

              /*
                Thanh tổng của nhóm — APM-GANTT-f.

                Một dải mảnh có ngoặc ở hai đầu, trải từ việc sớm nhất tới việc
                muộn nhất trong nhóm. Nó trả lời câu hỏi mà mười lăm thanh con
                không trả lời được khi cuộn: **pha này kéo dài từ bao giờ đến bao
                giờ**.

                Vẽ mảnh và sẫm màu, khác hẳn thanh việc: nó không phải một việc,
                không kéo được, và không mang tiến độ. Nhầm nó với một thanh việc
                là nhầm một cái hộp với một công việc — đúng cái nhầm mà bảng
                `task_group` sinh ra để tránh.
              */
              if (row.kind === 'group') {
                const span = row.group ? groupSpan.get(row.group.id) : undefined;
                if (!span) return null;

                const gx = dateToX(scale, span.start);
                const gw = Math.max(dateToX(scale, addDays(span.due, 1)) - gx, 2);
                const gy = index * ROW_HEIGHT + ROW_HEIGHT / 2 - 1;

                return (
                  <g key={`group-${row.group!.id}`} className="pointer-events-none">
                    <rect x={gx} y={gy} width={gw} height={3} className="fill-foreground/70" />
                    {/* Ngoặc hai đầu: dấu hiệu quy ước của một thanh tổng. */}
                    <polygon
                      points={`${gx},${gy} ${gx + 5},${gy} ${gx},${gy + 7}`}
                      className="fill-foreground/70"
                    />
                    <polygon
                      points={`${gx + gw},${gy} ${gx + gw - 5},${gy} ${gx + gw},${gy + 7}`}
                      className="fill-foreground/70"
                    />
                  </g>
                );
              }

              if (row.kind !== 'bar') return null;
              const bar = row.bar;
              const range = rangeOf(bar);
              if (!range) return null;

              const y = index * ROW_HEIGHT + (ROW_HEIGHT - BAR_HEIGHT) / 2;
              const x = dateToX(scale, range.start);
              // Ngày kết thúc là ngày **có làm việc**, nên thanh phủ hết ô đó.
              const width = Math.max(dateToX(scale, addDays(range.due, 1)) - x, scale.dayWidth / 2);

              if (bar.kind !== 'task') {
                return (
                  <Milestone
                    key={bar.task.id}
                    bar={bar}
                    today={today}
                    x={x}
                    y={y}
                    size={BAR_HEIGHT}
                    onSelect={() => {
                      // Cùng luật với thanh việc: vừa kéo xong thì cú click này
                      // là hệ quả của thao tác kéo, không phải ý định.
                      if (dragged.current) {
                        dragged.current = false;
                        return;
                      }
                      onSelect?.(bar.task.id);
                      onOpen?.(bar.task.id);
                    }}
                    {...(editable
                      ? {
                          onPointerDown: (event: React.PointerEvent) =>
                            beginDrag(bar, 'move', event),
                        }
                      : {})}
                    /*
                      Mốc vừa là đích thả vừa là điểm bắt đầu của một phụ thuộc
                      — APM-GANTT-e.

                      Trước đây cả hai đều không được: `onPointerUp` để thả chỉ
                      có trên `<rect>` của thanh việc, và đầu nối cũng chỉ vẽ ở
                      nhánh ấy. Mà mốc lại đúng là thứ hay đứng ở hai đầu một
                      quan hệ phụ thuộc nhất — "xong việc này thì tới mốc bàn
                      giao" là câu phổ biến nhất trong một kế hoạch Waterfall.
                    */
                    onPointerUp={() => dropLink(bar.task.id)}
                    onPointerEnter={() => {
                      if (linking) setHoverTarget(bar.task.id);
                    }}
                    onPointerLeave={() => setHoverTarget((id) => (id === bar.task.id ? null : id))}
                    highlighted={
                      linking !== null &&
                      linking.fromTaskId !== bar.task.id &&
                      hoverTarget === bar.task.id
                    }
                    {...(onLink
                      ? {
                          onStartLink: (event: React.PointerEvent) => {
                            event.stopPropagation();
                            dragged.current = false;
                            setLinking({
                              fromTaskId: bar.task.id,
                              ...svgPoint(
                                event,
                                (event.currentTarget as SVGElement).ownerSVGElement,
                              ),
                            });
                          },
                        }
                      : {})}
                  />
                );
              }

              return (
                /*
                  Làm chìm **cả cụm** của một thanh — APM-BOARD-h.

                  Đặt `opacity` ở `<g>` chứ không ở từng hình bên trong: một
                  thanh gồm nền, phần đã xong, thanh baseline, tay cầm co giãn
                  và đầu nối — làm nhạt từng cái là năm chỗ phải nhớ giữ khớp
                  nhau, và chỗ nào quên thì nó sáng lên giữa một hàng đang chìm.
                */
                <g key={bar.task.id} opacity={dimmedByFilter.has(bar.task.id) ? 0.3 : undefined}>
                  {bar.baselineStartDate && bar.baselineDueDate ? (
                    <rect
                      x={dateToX(scale, parseDate(bar.baselineStartDate))}
                      y={y + BAR_HEIGHT - 3}
                      width={
                        dateToX(scale, addDays(parseDate(bar.baselineDueDate), 1)) -
                        dateToX(scale, parseDate(bar.baselineStartDate))
                      }
                      height={4}
                      className="fill-muted-foreground/40"
                    >
                      <title>{t('gantt.baselineTitle')}</title>
                    </rect>
                  ) : null}

                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={BAR_HEIGHT}
                    rx={3}
                    {...(editable
                      ? {
                          onPointerDown: (event: React.PointerEvent) =>
                            beginDrag(bar, 'move', event),
                        }
                      : {})}
                    onPointerUp={() => dropLink(bar.task.id)}
                    onPointerEnter={() => {
                      if (linking) setHoverTarget(bar.task.id);
                    }}
                    onPointerLeave={() => setHoverTarget((id) => (id === bar.task.id ? null : id))}
                    onClick={() => {
                      // Vừa kéo xong thì cú click này là hệ quả của thao tác
                      // kéo, không phải ý định của người dùng.
                      if (dragged.current) {
                        dragged.current = false;
                        return;
                      }
                      onSelect?.(bar.task.id);
                      onOpen?.(bar.task.id);
                    }}
                    className={cn(
                      editable ? 'cursor-grab' : 'cursor-pointer',
                      /*
                        Màu thân thanh = **tình trạng lịch**, xem
                        `schedule-health.ts` để biết vì sao đường găng và mất
                        người không còn tranh chỗ ở kênh này.

                        Vi phạm phụ thuộc đứng trên tất cả, kể cả "đã xong":
                        một việc xong **trước** tiền nhiệm của nó là dữ liệu
                        sai, và đóng việc lại không làm nó đúng lên.
                      */
                      /*
                        Lịch sai đè lên **mọi** mức trạng thái: nó không phải
                        một mức nặng hơn, nó là "kế hoạch này không thực hiện
                        được". Lớp `gantt-violation-bar` tự đặt `fill` bằng
                        animation nên nó thắng lớp màu ở trên mà không cần
                        `!important`.
                      */
                      bar.violationDays > 0
                        ? 'gantt-violation-bar'
                        : HEALTH_FILL[scheduleHealth(bar, today)],
                      /*
                        Viền gạch cho việc mất người: cùng màu với "đang trên
                        đường găng" (amber) và "vi phạm phụ thuộc" (đỏ), nên chỉ
                        đổi màu là không phân biệt được ba chuyện. Nét gạch nói
                        "chuyện này về **người**, không về lịch".
                      */
                      impactedById.has(bar.task.id) &&
                        '[stroke-dasharray:3_2] stroke-2 stroke-foreground/70',
                      selectedId === bar.task.id && 'stroke-foreground',
                      // Đích thả sáng lên trong lúc nối.
                      // Chỉ đích **đang rê qua** sáng lên, không phải mọi
                      // thanh còn lại — xem `hoverTarget`.
                      linking &&
                        linking.fromTaskId !== bar.task.id &&
                        hoverTarget === bar.task.id &&
                        'stroke-2 stroke-primary',
                    )}
                  >
                    <title>{barTitle(t, bar, impactedById.get(bar.task.id))}</title>
                  </rect>

                  {/*
                    Lịch sai: gạch chéo đè lên, giữ nguyên màu trạng thái bên
                    dưới. `pointer-events-none` để nó không nuốt cú bấm và cú
                    kéo của chính thanh nằm dưới.
                  */}
                  {/* Phần đã hoàn thành, vẽ đè lên. */}
                  {bar.task.progressPct > 0 ? (
                    <rect
                      x={x}
                      y={y + BAR_HEIGHT - 5}
                      width={(width * bar.task.progressPct) / 100}
                      height={4}
                      className="pointer-events-none fill-background/70"
                    />
                  ) : null}

                  <rect
                    x={x}
                    y={y}
                    width={HANDLE_WIDTH}
                    height={BAR_HEIGHT}
                    className={cn('fill-transparent', editable && 'cursor-ew-resize')}
                    {...(editable
                      ? {
                          onPointerDown: (event: React.PointerEvent) =>
                            beginDrag(bar, 'resize-start', event),
                        }
                      : {})}
                  />
                  <rect
                    x={x + width - HANDLE_WIDTH}
                    y={y}
                    width={HANDLE_WIDTH}
                    height={BAR_HEIGHT}
                    className={cn('fill-transparent', editable && 'cursor-ew-resize')}
                    {...(editable
                      ? {
                          onPointerDown: (event: React.PointerEvent) =>
                            beginDrag(bar, 'resize-end', event),
                        }
                      : {})}
                  />

                  {/*
                    Đầu nối, tách khỏi tay cầm co giãn.
                    
                    Cùng một mép thanh phải làm hai việc khác nhau (kéo dài lịch
                    và nối sang task khác), nên chúng phải là hai chỗ bấm khác
                    nhau — nếu không, mỗi cú kéo mép là một lần đoán ý người
                    dùng. Đầu nối nhô ra ngoài thanh và có hình tròn.
                  */}
                  {/*
                    Ngày ở hai đầu, **chỉ trong lúc kéo** — APM-GANTT-b.

                    Kéo một thanh là đang chọn ngày, nhưng thứ duy nhất phản hồi
                    lại là vị trí của thanh trên một cái lưới — muốn biết mình
                    vừa chọn ngày nào thì phải đếm ô, và ở mức thu nhỏ (`week`,
                    `month`) thì đếm cũng không ra. Nhãn nói thẳng ra hai đầu.

                    Không hiện thường trực: mỗi thanh thêm hai cụm chữ thì biểu
                    đồ ba chục việc thành một bức tường số, và ngày của những
                    việc mình **không** đụng tới không phải thứ đang cần đọc.
                  */}
                  {drag?.taskId === bar.task.id && preview ? (
                    <DragDateLabels
                      startLabel={shortDate(preview.startDate)}
                      dueLabel={shortDate(preview.dueDate)}
                      x={x}
                      y={y}
                      width={width}
                      height={BAR_HEIGHT}
                    />
                  ) : null}

                  {onLink ? (
                    <circle
                      cx={x + width + 5}
                      cy={y + BAR_HEIGHT / 2}
                      r={4}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        dragged.current = false;
                        setLinking({
                          fromTaskId: bar.task.id,
                          ...svgPoint(event, event.currentTarget.ownerSVGElement),
                        });
                      }}
                      className="cursor-crosshair fill-background stroke-primary"
                      strokeWidth={2}
                    >
                      <title>{t('gantt.linkHint')}</title>
                    </circle>
                  ) : null}
                </g>
              );
            })}
            {/* Chốt bỏ liên kết vẽ **sau** thanh để nó nằm trên cùng. */}
            {onUnlink
              ? view.links.map((link) => (
                  <LinkHandle
                    key={`unlink-${link.id}`}
                    link={link}
                    bars={bars}
                    rowIndex={rowIndexOf}
                    scale={scale}
                    onRemove={() => onUnlink(link.id)}
                  />
                ))
              : null}
          </svg>

          {compact ? renderEdgeHints() : null}
        </div>
      </div>
      {confirmMove ? (
        <MilestoneMoveDialog
          title={view.bars.find((bar) => bar.task.id === confirmMove.taskId)?.task.title ?? ''}
          from={confirmMove.from}
          to={confirmMove.to}
          onCancel={() => {
            setConfirmMove(null);
            // Trả hình thoi về chỗ cũ: huỷ nghĩa là không có gì đổi, và để nó
            // nằm ở chỗ mới là nói dối về trạng thái đã lưu.
            setPreview(null);
          }}
          onConfirm={(date: string) => {
            setConfirmMove(null);
            setPreview(null);
            // Mốc có **một** ngày (thời lượng 0), nên hai cột mang cùng giá trị.
            onReschedule(confirmMove.taskId, { startDate: date, dueDate: date });
          }}
        />
      ) : null}
    </div>
  );

  /** Toạ độ đầu nối của một task, để vẽ đường kéo từ đúng chỗ nó bắt đầu. */
  function linkOrigin(taskId: string): { x: number; y: number } | null {
    const index = rowIndexOf.get(taskId);
    const row = index === undefined ? undefined : rows[index];
    const bar = row?.kind === 'bar' ? row.bar : undefined;
    if (bar === undefined || index === undefined || !bar.dueDate) return null;

    return {
      x: dateToX(scale, addDays(parseDate(bar.dueDate), 1)) + 5,
      y: index * ROW_HEIGHT + ROW_HEIGHT / 2,
    };
  }
}

/**
 * Một ô tiêu đề cột, kéo mép phải để đổi bề rộng — APM-GANTT-i.
 *
 * ## Kéo bằng pointer event, không phải `<input type="range">` hay thư viện
 *
 * Việc cần làm ở đây đúng một phép trừ: bề rộng mới = bề rộng lúc bấm + quãng
 * con trỏ đã đi. Kéo cả một thư viện bảng vào để có phép trừ ấy là đổi một
 * nhánh phụ thuộc lấy mười dòng.
 *
 * ⚠️ `setPointerCapture` để mép vẫn theo tay khi con trỏ chạy ra ngoài ô — bỏ
 * nó thì cú kéo **dừng giữa chừng** đúng lúc người dùng kéo nhanh, và bề rộng
 * đứng lại ở một chỗ họ không chọn.
 */
export function ColumnHeader({
  label,
  width,
  onResize,
}: {
  label: string;
  width: number;
  onResize: (width: number) => void;
}): ReactNode {
  const from = useRef<{ x: number; width: number } | null>(null);

  return (
    <span style={{ width }} className="relative shrink-0 truncate text-center">
      {/*
        Tên cột căn **giữa** hai mép kéo của chính nó — mép trái là mép phải
        của cột trước (hai cột liền kề chung một đường), mép phải là đường kẻ
        dưới đây. Căn theo hướng dữ liệu (số thì lệch phải) chỉ hợp cho ô dữ
        liệu, còn tiêu đề đứng giữa khung của nó dễ đọc hơn khi kéo hẹp lại.
      */}
      {label}

      {/*
        Mép kéo nhô ra hai bên đường kẻ: một vùng bắt chuột rộng 8px cho một
        đường kẻ 1px, vì không ai trỏ trúng một pixel.

        Đường kẻ mờ sẵn ở mọi lúc — đủ để thấy "có gì đó kéo được" mà không
        gào lên giữa một hàng tiêu đề toàn chữ nhạt màu.
      */}
      <span
        role="separator"
        aria-orientation="vertical"
        aria-label={label}
        onPointerDown={(event) => {
          event.preventDefault();
          // Ghi mốc **trước**, bắt con trỏ sau: không phải môi trường nào cũng
          // có `setPointerCapture` (jsdom không có), và thiếu nó thì kéo vẫn
          // chạy — chỉ kém mượt khi con trỏ ra ngoài ô. Gọi trước rồi để nó ném
          // là đánh đổi cả thao tác lấy một tiện nghi (cùng bẫy ở `beginDrag`).
          from.current = { x: event.clientX, width };
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (!from.current) return;
          const next = from.current.width + (event.clientX - from.current.x);
          onResize(Math.max(MIN_COLUMN_WIDTH, Math.round(next)));
        }}
        onPointerUp={() => {
          from.current = null;
        }}
        className="absolute -right-1 top-0 flex h-full w-2 cursor-col-resize items-stretch justify-center"
      >
        <span className="w-1 bg-primary/25 hover:bg-primary/50" />
      </span>
    </span>
  );
}

/**
 * Hàng tiêu đề của một nhóm — **in đậm**, có số việc, gấp được, đổi tên và xoá
 * được ngay tại chỗ.
 *
 * Nhóm không có ngày và không có thanh: nó là một cái hộp có tên, không phải
 * một việc. Nên hàng này chỉ chiếm chỗ ở cột nhãn, còn bên biểu đồ là một dải
 * trống — và đó là **đúng**, vì không có gì để vẽ.
 */
function GroupHeaderRow({
  group,
  collapsed,
  onToggle,
  onRename,
  onDelete,
}: {
  group: TaskGroupView | null;
  collapsed: boolean;
  onToggle: () => void;
  onRename?: (groupId: string, name: string) => void;
  onDelete?: (groupId: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(group?.name ?? '');

  return (
    <div
      style={{ height: ROW_HEIGHT }}
      className="group/row flex items-center gap-1.5 border-b border-border bg-muted/60 px-2 text-xs"
    >
      {group === null ? (
        <span className="font-semibold text-muted-foreground">{t('gantt.ungrouped')}</span>
      ) : renaming && onRename ? (
        <form
          className="flex flex-1 items-center gap-1"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = name.trim();
            if (trimmed.length > 0 && trimmed !== group.name) onRename(group.id, trimmed);
            setRenaming(false);
          }}
        >
          <input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            aria-label={t('gantt.groupName')}
            className="h-6 min-w-0 flex-1 rounded-control border border-border-strong bg-background px-1"
          />
          <button type="submit" className="shrink-0 font-medium text-primary">
            {t('common.save')}
          </button>
        </form>
      ) : (
        <>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!collapsed}
            aria-label={t('gantt.toggleGroup', { name: group.name })}
            className="shrink-0 text-muted-foreground"
          >
            {collapsed ? '▸' : '▾'}
          </button>

          <span className="truncate font-semibold">{group.name}</span>
          <span className="shrink-0 text-[10px] text-muted-foreground">({group.taskCount})</span>

          {onRename ? (
            <button
              type="button"
              onClick={() => {
                setName(group.name);
                setRenaming(true);
              }}
              className="ml-auto shrink-0 text-muted-foreground opacity-0 hover:text-foreground group-hover/row:opacity-100"
              aria-label={t('gantt.renameGroup', { name: group.name })}
            >
              ✎
            </button>
          ) : null}

          {onDelete ? (
            <button
              type="button"
              onClick={() => onDelete(group.id)}
              className="shrink-0 text-muted-foreground opacity-0 hover:text-destructive group-hover/row:opacity-100"
              aria-label={t('gantt.deleteGroup', { name: group.name })}
            >
              ×
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * Chốt bỏ liên kết, đặt ở **khớp** của mũi tên.
 *
 * ⚠️ Không dùng chính đường mũi tên làm chỗ bấm: mũi tên được vẽ **trước** các
 * thanh nên thanh nằm trên nó, và một cú bấm vào đoạn mũi tên đi ngang qua
 * thanh sẽ mở chi tiết task thay vì bỏ liên kết — đúng thứ đã xảy ra khi thử
 * trên trình duyệt. Chốt riêng thì nó nằm trên cùng và không tranh chỗ với ai:
 * khớp mũi tên gần như luôn rơi vào khoảng trống giữa hai thanh.
 */
function LinkHandle({
  link,
  bars,
  rowIndex,
  scale,
  onRemove,
}: {
  link: GanttLink;
  bars: GanttBar[];
  rowIndex: Map<string, number>;
  scale: ReturnType<typeof createScale>;
  onRemove: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const geometry = arrowGeometry(link, bars, rowIndex, scale);
  if (!geometry) return null;

  const { x: mid, y } = geometry.anchor;

  return (
    <g className="cursor-pointer" onClick={onRemove}>
      <circle cx={mid} cy={y} r={6} className="fill-background stroke-muted-foreground" />
      <path
        d={`M ${mid - 2.5} ${y - 2.5} L ${mid + 2.5} ${y + 2.5} M ${mid + 2.5} ${y - 2.5} L ${mid - 2.5} ${y + 2.5}`}
        className="stroke-muted-foreground"
        strokeWidth={1.5}
      />
      <title>{t('gantt.unlinkHint')}</title>
    </g>
  );
}

/** Hình học của một mũi tên. `null` khi thiếu hàng hoặc thiếu ngày. */
/**
 * Thanh của một task, tra theo **id**.
 *
 * ## ⚠️ Vì sao không dùng `bars[rowIndex.get(id)]`
 *
 * `rowIndex` đếm trên `rows`, mà `rows` xen **hàng tiêu đề nhóm** giữa các
 * thanh (xem chỗ dựng `rowIndexOf`). `bars` thì chỉ có thanh. Hai mảng vì thế
 * chỉ trùng chỉ số khi dự án **không có nhóm việc nào** — và đúng vì thế mà lỗi
 * này ẩn được lâu: mỗi nhóm đẩy lệch thêm một bậc, tới lúc nào đó
 * `bars[toRow]` thành `undefined`, và cả trang Gantt chết với "Cannot read
 * properties of undefined (reading 'startDate')". Phát hiện ở APM-1318d, khi bộ
 * dữ liệu trưng bày lần đầu có cả nhóm việc lẫn phụ thuộc.
 *
 * Chỉ số hàng vẫn **đúng** cho toạ độ dọc (`y1`/`y2`): hàng tiêu đề chiếm chỗ
 * thật trên màn hình. Chỉ phép tra thanh là sai.
 */
function barOf(bars: GanttBar[], taskId: string): GanttBar | undefined {
  return bars.find((bar) => bar.task.id === taskId);
}

function arrowGeometry(
  link: GanttLink,
  bars: GanttBar[],
  rowIndex: Map<string, number>,
  scale: ReturnType<typeof createScale>,
): { anchor: { x: number; y: number } } | null {
  const fromRow = rowIndex.get(link.fromTaskId);
  const toRow = rowIndex.get(link.toTaskId);
  if (fromRow === undefined || toRow === undefined) return null;

  // ⚠️ Tra thanh theo **id**, không phải theo chỉ số hàng — xem `barOf`.
  const from = barOf(bars, link.fromTaskId);
  const to = barOf(bars, link.toTaskId);
  if (!from?.dueDate || !to?.startDate) return null;

  const x1 = dateToX(scale, addDays(parseDate(from.dueDate), 1));
  const x2 = dateToX(scale, parseDate(to.startDate));
  const y1 = fromRow * ROW_HEIGHT + ROW_HEIGHT / 2;
  const y2 = toRow * ROW_HEIGHT + (ROW_HEIGHT - BAR_HEIGHT) / 2;

  /*
    ⚠️ Điểm neo lấy từ **chính** hàm vẽ đường — APM-GANTT-g.

    Trước đây hàm này tự tính lại `mid = x1 + 8`, đúng với hình cũ (đoạn dọc chạy
    ở `x1 + 8`). Khi đường đổi hình thì đoạn dọc chuyển sang `x2`, mà con số ở
    đây không đổi theo — nên nút X trôi ra giữa khoảng trắng, không nằm trên
    đường nào cả. Hai hàm tự tính lại cùng một hình học thì sớm muộn cũng lệch;
    nay chỉ còn một nguồn.
  */
  return { anchor: elbowRoute(x1, y1, x2, y2).anchor };
}

/**
 * Tooltip của một thanh: vì sao nó có màu đó.
 *
 * Ba câu, theo đúng thứ tự nghiêm trọng: vi phạm phụ thuộc (kế hoạch sai) →
 * mất người (kế hoạch đúng nhưng không ai làm) → còn bao nhiêu buffer. Ghép
 * chúng lại thành một đoạn dài thì người đọc phải tự tìm câu quan trọng.
 */
function barTitle(t: TFunction, bar: GanttBar, impacted: ScheduleImpactTask | undefined): string {
  if (bar.violationDays > 0) return t('gantt.violationTitle', { days: bar.violationDays });

  if (impacted) {
    const reason = t(`impact.reason.${impacted.reason}`, { name: impacted.assigneeName });
    // Người đã đi thì không có "thêm mấy ngày": việc phải sang tay người khác.
    const numbers = impacted.unassignable
      ? t('impact.needsReassign')
      : impacted.extraDays > 0
        ? t('impact.needsMoreDays', { days: impacted.extraDays })
        : t('impact.stillFits', { hours: impacted.availableHours });
    return `${reason} — ${numbers}`;
  }

  return doneLateTitle(t, bar) ?? t('gantt.barTitle', { float: bar.totalFloat });
}

/** "Hoàn thành ngày X, sau hạn Y" — `null` khi việc không xong trễ. */
function doneLateTitle(t: TFunction, bar: GanttBar): string | null {
  const { statusCategory, actualEndAt } = bar.task;
  if (!isCompletedLate(bar.dueDate, statusCategory, actualEndAt)) return null;
  return t('gantt.doneLateTitle', {
    done: displayDate(completedOn(statusCategory, actualEndAt)),
    due: displayDate(bar.dueDate),
  });
}

/**
 * Khoảng thời gian hiện ở cột nhãn. `null` = chưa xếp lịch.
 *
 * Mốc trả về **một** ngày: thời lượng của nó bằng 0, nên hai đầu mốc là cùng
 * một ngày và viết nó ra hai lần chỉ làm cột hẹp hơn mà không nói thêm gì.
 *
 * ⚠️ Đọc `dueDate` chứ không `startDate` cho mốc: hai cột luôn bằng nhau ở dữ
 * liệu mới, nhưng dữ liệu cũ (trước APM-TASK-f) có thể lệch — và với một mốc thì
 * ngày **đến hạn** mới là ngày người ta cam kết.
 */
function barPeriod(bar: GanttBar): string | null {
  if (bar.kind !== 'task') return bar.dueDate ? shortDate(bar.dueDate) : null;
  if (!bar.startDate || !bar.dueDate) return null;
  return `${shortDate(bar.startDate)}–${shortDate(bar.dueDate)}`;
}

function spanDays(bar: GanttBar): string {
  if (bar.kind !== 'task' || !bar.startDate || !bar.dueDate) return '—';

  const days =
    Math.round(
      (parseDate(bar.dueDate).getTime() - parseDate(bar.startDate).getTime()) / 86_400_000,
    ) + 1;
  return String(Math.max(1, days));
}

/** `2026-04-08` → `08/04`. Năm bỏ đi: cả cột nằm trong cùng một cửa sổ. */
function shortDate(iso: string): string {
  const [, month, day] = iso.split('-');
  return `${day}/${month}`;
}

/**
 * Ngày bắt đầu và ngày kết thúc dán vào hai đầu thanh đang kéo — APM-GANTT-b.
 *
 * ## `pointer-events: none` là bắt buộc, không phải tinh chỉnh
 *
 * Nhãn nằm **trong** vùng con trỏ đang đi qua. Để nó nhận sự kiện thì cú thả
 * rơi vào chữ chứ không vào thanh bên dưới, và `onPointerUp` của thanh không
 * bao giờ chạy — thao tác kéo im lặng không có tác dụng, đúng ở những lần kéo
 * xa nhất (lúc nhãn chạy tới dưới ngón tay).
 *
 * ## Nền vẽ bằng `rect`, không phải `paint-order: stroke`
 *
 * Chữ nằm đè lên dải cuối tuần, lưới ngày và cả thanh khác. Viền chữ giúp đọc
 * được trên nền sáng nhưng vẫn nhoè trên nền có sọc, còn một hình chữ nhật đục
 * thì đọc được ở mọi nền — và nó cũng là thứ duy nhất chạy giống nhau trên mọi
 * trình duyệt.
 */
function DragDateLabels({
  startLabel,
  dueLabel,
  x,
  y,
  width,
  height,
}: {
  startLabel: string;
  dueLabel: string;
  x: number;
  y: number;
  width: number;
  height: number;
}): ReactNode {
  const cy = y + height / 2;

  return (
    <g className="pointer-events-none">
      <DateChip label={startLabel} x={x - 6} y={cy} anchor="end" />
      <DateChip label={dueLabel} x={x + width + 6} y={cy} anchor="start" />
    </g>
  );
}

/** Một nhãn: hộp nền + chữ. Bề rộng ước theo số ký tự — SVG không tự co hộp. */
function DateChip({
  label,
  x,
  y,
  anchor,
}: {
  label: string;
  x: number;
  y: number;
  anchor: 'start' | 'end';
}): ReactNode {
  const boxWidth = label.length * 6 + 8;

  return (
    <>
      <rect
        x={anchor === 'end' ? x - boxWidth : x}
        y={y - 8}
        width={boxWidth}
        height={16}
        rx={3}
        className="fill-foreground"
      />
      <text
        x={anchor === 'end' ? x - 4 : x + 4}
        y={y + 4}
        textAnchor={anchor}
        className="fill-background text-[10px] font-medium tabular-nums"
      >
        {label}
      </text>
    </>
  );
}

function Milestone({
  bar,
  today,
  x,
  y,
  size,
  onSelect,
  onPointerDown,
  onPointerUp,
  onPointerEnter,
  onPointerLeave,
  onStartLink,
  highlighted = false,
}: {
  bar: GanttBar;
  x: number;
  y: number;
  size: number;
  /** Mốc thời gian để xét tình trạng lịch — từ `asOf` (luật #7). */
  today: Date;
  onSelect: () => void;
  /** Bỏ trống = không kéo được (người xem không có quyền sửa). */
  onPointerDown?: ((event: React.PointerEvent) => void) | undefined;
  onPointerUp?: (() => void) | undefined;
  onPointerEnter?: (() => void) | undefined;
  onPointerLeave?: (() => void) | undefined;
  /** Bỏ trống = không nối phụ thuộc được từ mốc này. */
  onStartLink?: ((event: React.PointerEvent) => void) | undefined;
  /** Con trỏ đang rê qua trong lúc nối — đích thả sắp tới. */
  highlighted?: boolean;
}): ReactNode {
  const half = size / 2;

  return (
    <g>
      <polygon
        points={`${x},${y} ${x + half},${y + half} ${x},${y + size} ${x - half},${y + half}`}
        onClick={onSelect}
        {...(onPointerDown ? { onPointerDown } : {})}
        {...(onPointerUp ? { onPointerUp } : {})}
        {...(onPointerEnter ? { onPointerEnter } : {})}
        {...(onPointerLeave ? { onPointerLeave } : {})}
        className={cn(
          onPointerDown ? 'cursor-grab' : 'cursor-pointer',
          /*
            Cùng thang màu với thanh việc (`schedule-health.ts`), chỉ khác ở mức
            "đúng tiến độ": mốc giữ màu chữ đậm chứ không xanh dương, vì hình
            thoi nhỏ hơn thanh nhiều lần và màu nhạt thì gần như mất hút.

            Không dùng `/70` cho mốc đã đạt: một hình thoi 16px mà mờ đi thì
            biến mất, trong khi cả nghĩa của mốc là **được nhìn thấy**.
          */
          bar.violationDays > 0
            ? 'gantt-violation-bar'
            : MILESTONE_FILL[scheduleHealth(bar, today)],
          highlighted && 'stroke-2 stroke-primary',
        )}
      >
        <title>{bar.task.title}</title>
      </polygon>

      {/*
        Đầu nối của mốc — cùng hình dạng với đầu nối của thanh việc, đặt ngay
        bên phải đỉnh phải để không đè lên chính hình thoi.
      */}
      {onStartLink ? (
        <circle
          cx={x + half + 6}
          cy={y + half}
          r={4}
          onPointerDown={onStartLink}
          className="cursor-crosshair fill-background stroke-primary"
          strokeWidth={2}
        />
      ) : null}
    </g>
  );
}

/**
 * Mũi tên phụ thuộc: từ mép phải task trước sang mép trái task sau.
 *
 * Vẽ dạng bậc thang (ngang → dọc → ngang) chứ không đường thẳng: đường thẳng
 * chéo qua nhiều hàng thì không lần được nó nối hai task nào.
 */
function DependencyArrow({
  link,
  bars,
  rowIndex,
  scale,
}: {
  link: GanttLink;
  bars: GanttBar[];
  rowIndex: Map<string, number>;
  scale: ReturnType<typeof createScale>;
}): ReactNode {
  const fromRow = rowIndex.get(link.fromTaskId);
  const toRow = rowIndex.get(link.toTaskId);
  if (fromRow === undefined || toRow === undefined) return null;

  // ⚠️ Tra thanh theo **id**, không phải theo chỉ số hàng — xem `barOf`.
  const from = barOf(bars, link.fromTaskId);
  const to = barOf(bars, link.toTaskId);
  if (!from?.dueDate || !to?.startDate) return null;

  const x1 = dateToX(scale, addDays(parseDate(from.dueDate), 1));
  const y1 = fromRow * ROW_HEIGHT + ROW_HEIGHT / 2;
  const x2 = dateToX(scale, parseDate(to.startDate));
  /*
    Đích là **đỉnh trên** của thanh kế tiếp, không phải giữa hàng — APM-GANTT-f.

    Mũi tên khi ấy cắm thẳng xuống đầu thanh, đúng chỗ công việc bắt đầu, thay
    vì đâm ngang vào sườn nó. Đây là hình dạng chuẩn của mọi công cụ Gantt và là
    thứ làm mũi tên đọc được khi nhiều đường chạy song song: hướng của đầu mũi
    tên nói ra **việc nào** bắt đầu, còn một đầu mũi tên nằm ngang giữa hàng thì
    không phân biệt được với một đường đi ngang qua.
  */
  const y2 = toRow * ROW_HEIGHT + (ROW_HEIGHT - BAR_HEIGHT) / 2;

  /*
    Cạnh **này** có đang bị vi phạm không — APM-GANTT-o.

    Xét từng cạnh chứ không đọc `to.violationDays`: con số ấy là mức vi phạm
    *nặng nhất* của task đích, nên một task có ba tiền nhiệm mà chỉ sai với một
    sẽ làm cả ba mũi tên nhấp nháy — và người dùng phải tự dò xem cạnh nào mới
    là cạnh phải sửa.

    So bằng chuỗi `YYYY-MM-DD`: đúng thứ tự từ điển = đúng thứ tự thời gian, và
    không phải dựng hai `Date` cho mỗi cạnh ở mỗi lần vẽ.
  */
  const violated = to.startDate < from.dueDate;

  return (
    <g
      className={cn(
        violated
          ? 'gantt-violation-link'
          : link.critical
            ? 'stroke-rag-red'
            : 'stroke-muted-foreground',
      )}
    >
      <path
        d={elbowRoute(x1, y1, x2, y2).path}
        fill="none"
        strokeWidth={violated || link.critical ? 2 : 1.5}
        strokeLinecap="round"
      />
      {/* Đầu mũi tên chỉ **xuống**, khớp với đoạn cuối của đường. */}
      <polygon
        points={`${x2},${y2} ${x2 - 3.5},${y2 - 5} ${x2 + 3.5},${y2 - 5}`}
        className={violated || link.critical ? 'fill-rag-red' : 'fill-muted-foreground'}
        stroke="none"
      />
    </g>
  );
}

/** Bán kính bo góc của mũi tên phụ thuộc. */
const ELBOW_RADIUS = 5;

/**
 * Đường gấp khúc từ mép phải thanh trước tới đỉnh trên thanh sau — APM-GANTT-f.
 *
 * ## Hai hình dạng, không phải một
 *
 * - **Thuận**: thanh sau bắt đầu *sau* thanh trước ⇒ đi ngang tới đúng cột của
 *   nó rồi cắm xuống. Ba đoạn.
 * - **Ngược**: thanh sau bắt đầu *trước* khi thanh trước xong (lịch đang vi
 *   phạm phụ thuộc, hoặc có `lag` âm) ⇒ phải vòng: nhô ra một đoạn, xuống nửa
 *   chừng giữa hai hàng, lùi lại, rồi mới cắm xuống. Năm đoạn.
 *
 * Không xử lý trường hợp ngược thì đường kẻ chạy **giật lùi** đè lên chính thanh
 * trước — và đó lại đúng là lúc người dùng cần đọc nó nhất, vì thanh đang đỏ.
 *
 * ## Góc bo bằng `Q`, không phải `A`
 *
 * Cung tròn (`A`) cần bảy tham số và một cờ hướng quét dễ đặt sai; đường cong
 * bậc hai với điểm điều khiển đặt ngay tại góc cho ra hình gần như không phân
 * biệt được, bằng một phần ba số ký tự.
 */
function elbowRoute(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): { path: string; anchor: { x: number; y: number } } {
  const r = ELBOW_RADIUS;
  const stub = 10;
  const down = y2 > y1 ? 1 : -1;

  if (x2 >= x1 + stub) {
    return {
      // Thuận: ngang → xuống → cắm vào đỉnh thanh.
      path: [
        `M ${x1} ${y1}`,
        `H ${x2 - r}`,
        `Q ${x2} ${y1} ${x2} ${y1 + r * down}`,
        `V ${y2}`,
      ].join(' '),
      /*
        Neo trên đoạn **ngang** khi nó đủ dài, còn không thì trên đoạn dọc.

        Đoạn ngang thường dài hơn hẳn (khoảng cách giữa hai việc) nên nút X ở
        giữa nó dễ trúng hơn. Nhưng hai việc nối sát nhau thì đoạn ấy chỉ vài
        pixel, và một nút 12px đặt lên đó sẽ đè cả hai thanh.
      */
      anchor: x2 - x1 > 24 ? { x: (x1 + x2) / 2, y: y1 } : { x: x2, y: (y1 + y2) / 2 },
    };
  }

  // Ngược: nhô ra, xuống nửa chừng, lùi lại, rồi cắm xuống.
  const turn = x1 + stub;
  const midY = (y1 + y2) / 2;

  return {
    path: [
      `M ${x1} ${y1}`,
      `H ${turn - r}`,
      `Q ${turn} ${y1} ${turn} ${y1 + r * down}`,
      `V ${midY - r * down}`,
      `Q ${turn} ${midY} ${turn - r} ${midY}`,
      `H ${x2 + r}`,
      `Q ${x2} ${midY} ${x2} ${midY + r * down}`,
      `V ${y2}`,
    ].join(' '),
    // Đoạn ngang ở giữa — chỗ duy nhất của hình này không đè lên thanh nào.
    anchor: { x: (turn + x2) / 2, y: midY },
  };
}

export { formatDate };
